import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CenteredLoadingSpinner from '../../components/CenteredLoadingSpinner';
import ResearchGrowthTables from './ResearchGrowthTables';
import type { FinancialDateMode } from '../../data/mockStockData';
import {
  fetchCashFlowStatement,
  fetchCashFlowStatementTTM,
  fetchIncomeStatement,
  fetchIncomeStatementTTM,
  fetchKeyMetrics,
  CashFlowStatement,
  IncomeStatement,
  KeyMetrics,
} from '../../services/stockApi';
import {
  fetchFiscalStandardizedCashFlowStatement,
  fetchFiscalStandardizedIncomeStatement,
  fetchFiscalStandardizedMetricsList,
  fetchFiscalStandardizedIncomeStatementTtm,
  fetchFiscalStandardizedCashFlowStatementTtm,
  fetchFiscalRoicRatios,
  fetchFiscalCompanyFilings,
  FiscalCashRow,
  FiscalIncomeRow,
  FiscalRatioRow,
  FiscalCompanyFilingRow,
} from '../../services/fiscalApi';

export type ResearchGrowthFiscalTablesMode = 'page' | 'embedded';

interface ResearchGrowthFiscalTablesProps {
  isDarkMode: boolean;
  selectedStock: string;
  selectedPeriod: 'annual' | 'quarter';
  mode?: ResearchGrowthFiscalTablesMode;
  analysisDateMode?: FinancialDateMode;
  asOfDate?: string | Date;
  rangeStart?: string | Date;
  rangeChangeSource?: 'date-range-container' | 'prev-next-quarter-year' | 'other';
  showSourceLabels?: boolean;

  showBookmarkButton?: boolean;
  isBookmarked?: boolean;
  isBookmarkBusy?: boolean;
  onBookmarkClick?: () => void;
}

const GROWTH_FISCAL_TIMEOUT_MS = 3000;
const GROWTH_FISCAL_CATALOG_TIMEOUT_MS = 900;
const GROWTH_FISCAL_EMBEDDED_TIMEOUT_MS = 5000;
const GROWTH_FISCAL_EMBEDDED_CATALOG_TIMEOUT_MS = 1500;

function withGrowthFiscalTimeout<T>(promise: Promise<T>, fallback: T, timeoutMs: number): Promise<T> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(fallback);
    }, timeoutMs);

    promise
      .then((value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(fallback);
      });
  });
}

const ResearchGrowthFiscalTables: React.FC<ResearchGrowthFiscalTablesProps> = ({
  isDarkMode,
  selectedStock,
  mode = 'page',
  analysisDateMode = 'as-reported',
  asOfDate,
  rangeStart,
  rangeChangeSource = 'other',
  showBookmarkButton = false,
  isBookmarked = false,
  isBookmarkBusy = false,
  onBookmarkClick,
  showSourceLabels = false,
}) => {
  const [incomeAnnual, setIncomeAnnual] = useState<FiscalIncomeRow[]>([]);
  const [incomeQuarter, setIncomeQuarter] = useState<FiscalIncomeRow[]>([]);
  const [cashAnnual, setCashAnnual] = useState<FiscalCashRow[]>([]);
  const [cashQuarter, setCashQuarter] = useState<FiscalCashRow[]>([]);
  const [incomeTtm, setIncomeTtm] = useState<FiscalIncomeRow | null>(null);
  const [cashTtm, setCashTtm] = useState<FiscalCashRow | null>(null);
  const [fmpIncomeAnnual, setFmpIncomeAnnual] = useState<IncomeStatement[]>([]);
  const [fmpIncomeQuarter, setFmpIncomeQuarter] = useState<IncomeStatement[]>([]);
  const [fmpCashAnnual, setFmpCashAnnual] = useState<CashFlowStatement[]>([]);
  const [fmpCashQuarter, setFmpCashQuarter] = useState<CashFlowStatement[]>([]);
  const [fmpIncomeTtm, setFmpIncomeTtm] = useState<IncomeStatement | null>(null);
  const [fmpCashTtm, setFmpCashTtm] = useState<CashFlowStatement | null>(null);
  const [fmpKeyMetricsAnnual, setFmpKeyMetricsAnnual] = useState<KeyMetrics[]>([]);
  const [fmpKeyMetricsQuarter, setFmpKeyMetricsQuarter] = useState<KeyMetrics[]>([]);
  const [roicRatios, setRoicRatios] = useState<FiscalRatioRow[]>([]);
  const [companyFilings, setCompanyFilings] = useState<FiscalCompanyFilingRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fiscalUnavailable, setFiscalUnavailable] = useState(false);
  const loadRequestIdRef = useRef(0);

  const normalizeInputDateKey = (value: string): string => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';

    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:\D.*)?$/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const ymd = raw.match(/^(\d{4})[\/.](\d{1,2})[\/.](\d{1,2})(?:\D.*)?$/);
    if (ymd) {
      const yyyy = ymd[1];
      const mm = ymd[2].padStart(2, '0');
      const dd = ymd[3].padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    const dmy = raw.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})(?:\D.*)?$/);
    if (dmy) {
      const dd = dmy[1].padStart(2, '0');
      const mm = dmy[2].padStart(2, '0');
      const yyyy = dmy[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    const ts = Date.parse(raw);
    if (!Number.isFinite(ts)) return '';
    return new Date(ts).toISOString().slice(0, 10);
  };

  const rangeEndTs = useMemo(() => {
    if (!asOfDate) return Number.POSITIVE_INFINITY;
    if (asOfDate instanceof Date) {
      const t = asOfDate.getTime();
      return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
    }
    const key = normalizeInputDateKey(String(asOfDate).trim());
    if (!key) return Number.POSITIVE_INFINITY;
    const t = Date.parse(`${key}T23:59:59.999Z`);
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  }, [asOfDate]);

  const rangeStartTs = useMemo(() => {
    if (!rangeStart) return Number.NEGATIVE_INFINITY;
    if (rangeStart instanceof Date) {
      const t = rangeStart.getTime();
      return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
    }
    const key = normalizeInputDateKey(String(rangeStart).trim());
    if (!key) return Number.NEGATIVE_INFINITY;
    const t = Date.parse(`${key}T00:00:00.000Z`);
    return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
  }, [rangeStart]);

  // Back Testing tables follow the chart's analysis date mode:
  // As Reported = period end date; At Reported = filing window.
  const useFilingWindowAnchors = mode === 'embedded' && Boolean(asOfDate) && analysisDateMode === 'at-reported';

  // Real SEC filing dates (from fiscal.ai's /v2/company/filings endpoint) keyed by
  // reported period-end date. The standardized financials endpoint does not reliably
  // include the true filing date, so this map is the source of truth for all symbols.
  const filingDateByReportKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of companyFilings) {
      const reportRaw = row?.reportDate || row?.report_date;
      const filingRaw = row?.filingDate || row?.filing_date;
      const reportKey = normalizeInputDateKey(String(reportRaw || ''));
      const filingKey = normalizeInputDateKey(String(filingRaw || ''));
      if (!reportKey || !filingKey) continue;

      const existing = map.get(reportKey);
      if (!existing || filingKey < existing) {
        // Prefer the earliest filing for a given period (original filing over amendments).
        map.set(reportKey, filingKey);
      }
    }
    return map;
  }, [companyFilings]);

  const hasRealFilingDateMap = filingDateByReportKey.size > 0;

  const toUtcDateKey = (value: string): string => {
    const key = normalizeInputDateKey(value);
    if (key) return key;

    const raw = String(value ?? '').trim();
    if (!raw) return '';
    const direct = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (direct) return direct[1];
    return '';
  };

  const parseAnchorDateAsUtcStart = (value: string): number => {
    const key = toUtcDateKey(value);
    if (!key) return Number.NaN;
    return Date.parse(`${key}T00:00:00.000Z`);
  };

  const addUtcMonths = (dateKey: string, months: number): string => {
    const key = toUtcDateKey(dateKey);
    if (!key) return '';
    const base = new Date(`${key}T00:00:00.000Z`);
    if (Number.isNaN(base.getTime())) return '';
    base.setUTCMonth(base.getUTCMonth() + months);
    return base.toISOString().slice(0, 10);
  };

  const filterByVisibleRange = useMemo(() => {
    return <T extends { date: string; filingDate?: string; acceptedDate?: string; reportDate?: string; earningDate?: string }>(rows: T[]): T[] => {
      const hasStart = Number.isFinite(rangeStartTs) && rangeStartTs !== Number.NEGATIVE_INFINITY;
      const hasEnd = Number.isFinite(rangeEndTs) && rangeEndTs !== Number.POSITIVE_INFINITY;
      if (!hasStart && !hasEnd) return rows;

      const strictEmbeddedAtReported = useFilingWindowAnchors;

      const getAtReportedAnchorDate = (row: T): string => {
        // Fiscal back-testing must follow SEC filing date timing.
        // Prefer the real SEC filing date (from /v2/company/filings) matched by
        // reported period-end date; fallback to the row's own filingDate/report/date
        // only when the real filings map has no match, so tables stay populated.
        const reportKey = normalizeInputDateKey(String(row?.date || ''));
        const realFilingDate = reportKey ? filingDateByReportKey.get(reportKey) : undefined;
        if (strictEmbeddedAtReported && hasRealFilingDateMap) {
          return realFilingDate || '';
        }
        if (strictEmbeddedAtReported) {
          // If true filing windows are unavailable, use the standardized row filing
          // date (e.g. lastSourceFilingDate) before period-end date.
          return String(row?.filingDate || row?.reportDate || row?.date || '');
        }
        if (strictEmbeddedAtReported && realFilingDate) {
          return realFilingDate;
        }
        return String(row?.filingDate || row?.reportDate || row?.date || '');
      };

      const getAnchorDate = (row: T): string => {
        // In embedded Back Testing, prev/next quarter navigation is period-based.
        // Keep table slicing aligned to quarter period-end dates in this flow,
        // even when analysis mode is "at-reported".
        if (rangeChangeSource === 'prev-next-quarter-year' && analysisDateMode !== 'at-reported') {
          return String(row?.date || '');
        }

        if (useFilingWindowAnchors || analysisDateMode === 'at-reported') {
          return getAtReportedAnchorDate(row);
        }
        return String(row?.date || '');
      };

      return rows.filter((row) => {
        const t = parseAnchorDateAsUtcStart(getAnchorDate(row));
        if (!Number.isFinite(t)) return false;
        if (hasStart && t < rangeStartTs) return false;

        if (hasEnd) {
          if (t > rangeEndTs) return false;
        }

        return true;
      });
    };
  }, [analysisDateMode, filingDateByReportKey, hasRealFilingDateMap, rangeChangeSource, rangeEndTs, rangeStartTs, useFilingWindowAnchors]);

  useEffect(() => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    const fiscalTimeoutMs = mode === 'embedded' ? GROWTH_FISCAL_EMBEDDED_TIMEOUT_MS : GROWTH_FISCAL_TIMEOUT_MS;
    const fiscalCatalogTimeoutMs = mode === 'embedded'
      ? GROWTH_FISCAL_EMBEDDED_CATALOG_TIMEOUT_MS
      : GROWTH_FISCAL_CATALOG_TIMEOUT_MS;
    const annualHistoryLimit = 40;
    const quarterHistoryLimit = 80;

    const run = async () => {
      const [fmpIncomeA, fmpCashA, fmpIncomeQ, fmpCashQ, fmpIncomeTtmRow, fmpCashTtmRow, fmpKmAnnual, fmpKmQuarter] = await Promise.all([
        fetchIncomeStatement(selectedStock, 'annual', annualHistoryLimit),
        fetchCashFlowStatement(selectedStock, 'annual', annualHistoryLimit),
        fetchIncomeStatement(selectedStock, 'quarter', quarterHistoryLimit),
        fetchCashFlowStatement(selectedStock, 'quarter', quarterHistoryLimit),
        fetchIncomeStatementTTM(selectedStock, 1),
        fetchCashFlowStatementTTM(selectedStock, 1),
        fetchKeyMetrics(selectedStock, 'annual', annualHistoryLimit),
        fetchKeyMetrics(selectedStock, 'quarter', quarterHistoryLimit),
      ]);

      if (requestId !== loadRequestIdRef.current) return;

      setFmpIncomeAnnual(fmpIncomeA);
      setFmpCashAnnual(fmpCashA);
      setFmpIncomeQuarter(fmpIncomeQ);
      setFmpCashQuarter(fmpCashQ);
      setFmpIncomeTtm(fmpIncomeTtmRow);
      setFmpCashTtm(fmpCashTtmRow);
      setFmpKeyMetricsAnnual(fmpKmAnnual);
      setFmpKeyMetricsQuarter(fmpKmQuarter);
      setFiscalUnavailable(true);
      setLoading(false);

      const metrics = await withGrowthFiscalTimeout(
        fetchFiscalStandardizedMetricsList(),
        [],
        fiscalCatalogTimeoutMs
      );

      if (requestId !== loadRequestIdRef.current) return;

      const [income, cash, incomeTtmResult, cashTtmResult, filings] = await Promise.all([
        withGrowthFiscalTimeout(
          fetchFiscalStandardizedIncomeStatement(selectedStock, metrics),
          { annual: [], quarter: [], error: 'Fiscal income timed out.' },
          fiscalTimeoutMs
        ),
        withGrowthFiscalTimeout(
          fetchFiscalStandardizedCashFlowStatement(selectedStock, metrics),
          { annual: [], quarter: [], error: 'Fiscal cash flow timed out.' },
          fiscalTimeoutMs
        ),
        withGrowthFiscalTimeout(
          fetchFiscalStandardizedIncomeStatementTtm(selectedStock, metrics),
          { row: null, error: 'Fiscal income TTM timed out.' },
          fiscalTimeoutMs
        ),
        withGrowthFiscalTimeout(
          fetchFiscalStandardizedCashFlowStatementTtm(selectedStock, metrics),
          { row: null, error: 'Fiscal cash flow TTM timed out.' },
          fiscalTimeoutMs
        ),
        withGrowthFiscalTimeout(
          fetchFiscalCompanyFilings(selectedStock).catch(() => []),
          [],
          fiscalTimeoutMs
        ),
      ]);

      if (requestId !== loadRequestIdRef.current) return;

      setIncomeAnnual(income.annual);
      setIncomeQuarter(income.quarter);
      setCashAnnual(cash.annual);
      setCashQuarter(cash.quarter);
      setIncomeTtm(incomeTtmResult.row);
      setCashTtm(cashTtmResult.row);
      setCompanyFilings(Array.isArray(filings) ? filings : []);
      const fiscalError = income.error || cash.error || incomeTtmResult.error || cashTtmResult.error || null;
      const fiscalDataCount = income.annual.length + income.quarter.length + cash.annual.length + cash.quarter.length;
      const errorText = typeof fiscalError === 'string' ? fiscalError : '';
      const isTimeoutOnly = /timed out/i.test(errorText);
      const shouldShowErrorMessage = Boolean(errorText) && (!isTimeoutOnly || fiscalDataCount === 0);
      setErrorMessage(shouldShowErrorMessage ? errorText : null);
      const unavailableByMessage = Boolean(errorText) && /data is not yet available|companies-list|companies list/i.test(errorText);
      setFiscalUnavailable(unavailableByMessage || fiscalDataCount === 0);
    };

    run().catch(() => {
      if (requestId === loadRequestIdRef.current) {
        setErrorMessage('Fiscal API request failed.');
        setFiscalUnavailable(true);
        setLoading(false);
      }
    });

    return () => {
      // stale guard via request id
    };
  }, [mode, selectedStock]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const ratios = await fetchFiscalRoicRatios(selectedStock);
      if (cancelled) return;
      setRoicRatios(ratios);
    };

    run().catch(() => {
      if (!cancelled) setRoicRatios([]);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedStock]);

  const basePageStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    color: isDarkMode ? '#e5e7eb' : '#1f2937',
    fontFamily: 'Manrope, Arial, sans-serif',
    boxSizing: 'border-box',
  };

  const containerStyle: React.CSSProperties =
    mode === 'page'
      ? {
          ...basePageStyle,
          padding: '2rem',
          minHeight: '100vh',
        }
      : {
          ...basePageStyle,
          padding: '0.75rem 2.5rem 1.5rem 2.5rem',
          minHeight: 'unset',
        };

  const frameStyle: React.CSSProperties =
    mode === 'page'
      ? {
          ...containerStyle,
          border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
          padding: '0.3rem 0.5rem',
          borderRadius: '8px',
          width: '92%',
          marginLeft: '3rem',
        }
      : {
          ...containerStyle,
          borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
        };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    marginBottom: '2rem',
  };
  
  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '0.88rem',
    fontWeight: 300,
    margin: '0.5rem 0 0.5rem 0',
    color: isDarkMode ? '#fff' : '#0D0D0D',
  };

  const groupHeaderTitleStyle: React.CSSProperties = {
    fontSize: '0.88rem',
    fontWeight: 300,
  };

  const sourceLabelStyle: React.CSSProperties = {
    marginLeft: '0.4rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
  };

  const floatingSourceLabelStyle: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 0,
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
    pointerEvents: 'none',
  };

  const useFiscalData = !fiscalUnavailable;

  const combinedTableStyle: React.CSSProperties = {
    ...tableStyle,
    tableLayout: 'fixed',
  };

  const thStyle: React.CSSProperties = {
    background: isDarkMode ? '#1F2937' : '#f5f5f5',
    color: isDarkMode ? '#fff' : '#0D0D0D',
    fontWeight: 500,
    padding: '8px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    textAlign: 'center',
    fontSize: '14px',
  };

  const tdStyle: React.CSSProperties = {
    padding: '8px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    textAlign: 'center',
    background: isDarkMode ? '#111827' : '#ffffff',
    color: isDarkMode ? '#e5e7eb' : '#0D0D0D',
  };

  const leftTdStyle: React.CSSProperties = { ...tdStyle, textAlign: 'left', fontWeight: 600 };
  const dividerBorder = isDarkMode ? '2px solid #1F2937' : '2px solid #e0e0e0';
  const ttmDividerBorder = isDarkMode ? '3px solid #555' : '3px solid #999';
  const firstColWidthPx = 180;
  const firstColHeaderStyle: React.CSSProperties = { ...thStyle, width: firstColWidthPx, minWidth: firstColWidthPx, textAlign: 'left' };
  const firstColCellStyle: React.CSSProperties = { ...leftTdStyle, width: firstColWidthPx, minWidth: firstColWidthPx };

  const errorBannerStyle: React.CSSProperties = {
    background: isDarkMode ? '#1F2937' : '#FEF2F2',
    color: isDarkMode ? '#FCA5A5' : '#B91C1C',
    border: `1px solid ${isDarkMode ? '#374151' : '#FECACA'}`,
    borderRadius: 8,
    padding: '0.75rem 1rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  };

  const formatPeriodLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    const month = d.toLocaleString('en-US', { month: 'short' });
    const yy = String(d.getFullYear()).slice(-2);
    return `${month} ${yy}`;
  };

  const useAtReportedAnchors = useFilingWindowAnchors || (analysisDateMode === 'at-reported' && mode === 'embedded');

  const formatDisplayPeriodLabel = <T extends { date: string; filingDate?: string; acceptedDate?: string; reportDate?: string; earningDate?: string }>(
    row: T
  ) => {
    const key = String(row?.date || '');
    return formatPeriodLabel(key);
  };

  const formatStatementNumber = (value: number | undefined): string => {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '-';
    const abs = Math.abs(value);
    if (abs >= 1e9) return (value / 1e9).toFixed(2) + 'B';
    if (abs >= 1e6) return (value / 1e6).toFixed(2) + 'M';
    return value.toFixed(2);
  };

  const formatPercent = (value: number | undefined, fractionDigits: number = 1) => {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '-';
    return `${(value * 100).toFixed(fractionDigits)}%`;
  };

  const formatPercentFromAlreadyPercent = (value: number | undefined, fractionDigits: number = 1) => {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '-';
    return `${value.toFixed(fractionDigits)}%`;
  };

  const normalizeGrowthDisplayValue = (value: string): string => {
    const trimmed = (value ?? '').trim();
    if (!trimmed || trimmed === '-') return 'N/A';
    if (/nan/i.test(trimmed) || /infinity/i.test(trimmed)) return 'N/A';
    return trimmed;
  };

  const normalizeCagrTableValue = (value: string): string => {
    const normalized = normalizeGrowthDisplayValue(value);
    if (normalized === 'N/A') return 'N/A';
    const match = normalized.match(/-?\d+(?:\.\d+)?/);
    if (!match) return normalized;
    const numericPercent = Number(match[0]);
    if (!Number.isFinite(numericPercent)) return 'N/A';
    return normalized;
  };

  const getPercentCellStyle = (value: string): React.CSSProperties => {
    const v = (value ?? '').trim();
    if (v === 'N/A') return { color: 'red' };

    const match = v.match(/-?\d+(?:\.\d+)?/);
    if (!match) return {};
    const numericPercent = Number(match[0]);
    if (!Number.isFinite(numericPercent)) return {};
    if (numericPercent < 0) return { color: 'red' };
    return numericPercent < 10 ? { color: 'red' } : {};
  };

  const getNegativeNumberCellStyle = (value: number | undefined): React.CSSProperties => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return {};
    return value < 0 ? { color: 'red' } : {};
  };

  const getNegativeStringCellStyle = (value: string): React.CSSProperties => {
    const v = (value ?? '').trim();
    if (!v || v === '-' || v === 'N/A') return {};
    return v.startsWith('-') ? { color: 'red' } : {};
  };

  const warningValueWrapStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const renderWithWarningIcon = (value: React.ReactNode, style: React.CSSProperties): React.ReactNode => {
    const color = String(style?.color ?? '').trim().toLowerCase();
    const isRed = color === 'red' || color === '#ff0000' || color === '#f00';
    if (!isRed) return value;

    const text = typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '';
    if (!text || text === '-' || text === 'N/A') return value;

    const isNegative = text.startsWith('-');
    if (!isNegative) return value;

    return (
      <span style={warningValueWrapStyle}>
        <img src="/images/warning.svg" alt="" aria-hidden="true" style={{ width: 14, height: 14, display: 'block', flex: '0 0 auto' }} />
        <span>{value}</span>
      </span>
    );
  };

  const isFiniteNumber = (value: number | undefined) => typeof value === 'number' && Number.isFinite(value);

  const toFiscalIncome = (row: IncomeStatement | null | undefined): FiscalIncomeRow | null => {
    if (!row?.date) return null;
    const epsValue = (row as any)?.epsDiluted ?? row.eps;
    return {
      date: row.date,
      periodEndDate: row.date,
      filingDate: (row as any)?.filingDate,
      earningDate: (row as any)?.acceptedDate,
      revenue: row.revenue,
      grossProfit: row.grossProfit,
      operatingIncome: row.operatingIncome,
      netIncome: row.netIncome,
      eps: epsValue,
      epsDiluted: epsValue,
    };
  };

  const toFiscalCash = (row: CashFlowStatement | null | undefined): FiscalCashRow | null => {
    if (!row?.date) return null;
    const value = row.netCashProvidedByOperatingActivities ?? row.operatingCashFlow;
    return {
      date: row.date,
      periodEndDate: row.date,
      filingDate: (row as any)?.filingDate,
      earningDate: (row as any)?.acceptedDate,
      netIncome: row.netIncome,
      operatingCashFlow: value,
      netCashProvidedByOperatingActivities: value,
      capitalExpenditure: row.capitalExpenditure,
      investmentsInPropertyPlantAndEquipment: row.investmentsInPropertyPlantAndEquipment,
    };
  };

  const mergeIncomeRows = (fiscalRows: FiscalIncomeRow[], fmpRows: FiscalIncomeRow[]) => {
    const map = new Map<string, FiscalIncomeRow>();
    for (const row of fmpRows) {
      if (row?.date) map.set(row.date, { ...row });
    }
    for (const row of fiscalRows) {
      if (!row?.date) continue;
      const base = map.get(row.date);
      const merged: FiscalIncomeRow = {
        date: row.date,
        periodEndDate: row.periodEndDate ?? base?.periodEndDate,
        filingDate: row.filingDate ?? base?.filingDate,
        earningDate: row.earningDate ?? base?.earningDate,
        revenue: isFiniteNumber(row.revenue) ? row.revenue : base?.revenue,
        grossProfit: isFiniteNumber(row.grossProfit) ? row.grossProfit : base?.grossProfit,
        operatingIncome: isFiniteNumber(row.operatingIncome) ? row.operatingIncome : base?.operatingIncome,
        netIncome: isFiniteNumber(row.netIncome) ? row.netIncome : base?.netIncome,
        eps: isFiniteNumber(row.eps) ? row.eps : base?.eps,
        epsDiluted: isFiniteNumber(row.epsDiluted) ? row.epsDiluted : base?.epsDiluted,
      };
      map.set(row.date, merged);
    }
    return Array.from(map.values());
  };

  const mergeCashRows = (fiscalRows: FiscalCashRow[], fmpRows: FiscalCashRow[]) => {
    const map = new Map<string, FiscalCashRow>();
    for (const row of fmpRows) {
      if (row?.date) map.set(row.date, { ...row });
    }
    for (const row of fiscalRows) {
      if (!row?.date) continue;
      const base = map.get(row.date);
      const merged: FiscalCashRow = {
        date: row.date,
        periodEndDate: row.periodEndDate ?? base?.periodEndDate,
        filingDate: row.filingDate ?? base?.filingDate,
        earningDate: row.earningDate ?? base?.earningDate,
        netIncome: isFiniteNumber(row.netIncome) ? row.netIncome : base?.netIncome,
        operatingCashFlow: isFiniteNumber(row.operatingCashFlow) ? row.operatingCashFlow : base?.operatingCashFlow,
        netCashProvidedByOperatingActivities: isFiniteNumber(row.netCashProvidedByOperatingActivities)
          ? row.netCashProvidedByOperatingActivities
          : base?.netCashProvidedByOperatingActivities,
        capitalExpenditure: isFiniteNumber(row.capitalExpenditure) ? row.capitalExpenditure : base?.capitalExpenditure,
        investmentsInPropertyPlantAndEquipment: isFiniteNumber(row.investmentsInPropertyPlantAndEquipment)
          ? row.investmentsInPropertyPlantAndEquipment
          : base?.investmentsInPropertyPlantAndEquipment,
      };
      map.set(row.date, merged);
    }
    return Array.from(map.values());
  };

  const sortByDateDesc = <T extends { date: string; filingDate?: string; acceptedDate?: string; reportDate?: string; earningDate?: string }>(arr: T[]) =>
    [...arr]
      .filter((d) => d.date)
      .sort((a, b) => {
        const ta = Date.parse(toUtcDateKey(String(a.date || '')) || String(a.date || ''));
        const tb = Date.parse(toUtcDateKey(String(b.date || '')) || String(b.date || ''));

        const aValid = Number.isFinite(ta);
        const bValid = Number.isFinite(tb);

        if (aValid && bValid) return tb - ta;
        if (aValid && !bValid) return -1;
        if (!aValid && bValid) return 1;
        return String(b.date || '').localeCompare(String(a.date || ''));
      });

  const fmpIncomeAnnualRows = useMemo(
    () => fmpIncomeAnnual.map((row) => toFiscalIncome(row)).filter((row): row is FiscalIncomeRow => !!row),
    [fmpIncomeAnnual]
  );
  const fmpIncomeQuarterRows = useMemo(
    () => fmpIncomeQuarter.map((row) => toFiscalIncome(row)).filter((row): row is FiscalIncomeRow => !!row),
    [fmpIncomeQuarter]
  );
  const fmpCashAnnualRows = useMemo(
    () => fmpCashAnnual.map((row) => toFiscalCash(row)).filter((row): row is FiscalCashRow => !!row),
    [fmpCashAnnual]
  );
  const fmpCashQuarterRows = useMemo(
    () => fmpCashQuarter.map((row) => toFiscalCash(row)).filter((row): row is FiscalCashRow => !!row),
    [fmpCashQuarter]
  );

  const mergedIncomeAnnual = useMemo(() => {
    if (!useFiscalData) return fmpIncomeAnnualRows;
    // Fiscal mode must be fiscal-only (no FMP fallback blending).
    return incomeAnnual;
  }, [incomeAnnual, fmpIncomeAnnualRows, useFiscalData]);

  const mergedIncomeQuarter = useMemo(() => {
    if (!useFiscalData) return fmpIncomeQuarterRows;
    // Fiscal mode must be fiscal-only (no FMP fallback blending).
    return incomeQuarter;
  }, [incomeQuarter, fmpIncomeQuarterRows, useFiscalData]);

  const mergedCashAnnual = useMemo(() => {
    if (!useFiscalData) return fmpCashAnnualRows;
    // Fiscal mode must be fiscal-only (no FMP fallback blending).
    return cashAnnual;
  }, [cashAnnual, fmpCashAnnualRows, useFiscalData]);

  const mergedCashQuarter = useMemo(() => {
    if (!useFiscalData) return fmpCashQuarterRows;
    // Fiscal mode must be fiscal-only (no FMP fallback blending).
    return cashQuarter;
  }, [cashQuarter, fmpCashQuarterRows, useFiscalData]);

  const mergedIncomeAnnualEffective = useMemo(
    () => filterByVisibleRange(mergedIncomeAnnual),
    [filterByVisibleRange, mergedIncomeAnnual]
  );

  const mergedIncomeQuarterEffective = useMemo(
    () => filterByVisibleRange(mergedIncomeQuarter),
    [filterByVisibleRange, mergedIncomeQuarter]
  );

  const mergedCashAnnualEffective = useMemo(
    () => filterByVisibleRange(mergedCashAnnual),
    [filterByVisibleRange, mergedCashAnnual]
  );

  const mergedCashQuarterEffective = useMemo(
    () => filterByVisibleRange(mergedCashQuarter),
    [filterByVisibleRange, mergedCashQuarter]
  );

  const mergedIncomeTtm = useMemo(() => {
    const fiscalRow = incomeTtm ?? null;
    const fmpRow = toFiscalIncome(fmpIncomeTtm) ?? null;
    if (!useFiscalData) return fmpRow;
    // Fiscal mode must be fiscal-only (no FMP fallback blending).
    return fiscalRow;
  }, [incomeTtm, fmpIncomeTtm, useFiscalData]);

  const mergedCashTtm = useMemo(() => {
    const fiscalRow = cashTtm ?? null;
    const fmpRow = toFiscalCash(fmpCashTtm) ?? null;
    if (!useFiscalData) return fmpRow;
    // Fiscal mode must be fiscal-only (no FMP fallback blending).
    return fiscalRow;
  }, [cashTtm, fmpCashTtm, useFiscalData]);

  const annualIncomeCols = sortByDateDesc(mergedIncomeAnnualEffective).slice(0, 10).reverse();
  const quarterlyIncomeDescRaw = sortByDateDesc(mergedIncomeQuarterEffective);
  const quarterlyCashDescRaw = sortByDateDesc(mergedCashQuarterEffective);
  const quarterlyIncomeDesc = quarterlyIncomeDescRaw;
  const quarterlyCashDesc = quarterlyCashDescRaw;
  const quarterlyIncomeCols = quarterlyIncomeDesc.slice(0, 5).reverse();
  const annualCashCols = sortByDateDesc(mergedCashAnnualEffective);
  const quarterlyCashCols = quarterlyCashDesc;
  const annualIncomeAsc = sortByDateDesc(mergedIncomeAnnualEffective).reverse();
  const annualIncomeDesc = sortByDateDesc(mergedIncomeAnnualEffective);
  const annualCashDesc = sortByDateDesc(mergedCashAnnualEffective);

  const latestAnnualDateKey = useMemo(() => {
    if (annualIncomeCols.length === 0) return null;
    return annualIncomeCols[annualIncomeCols.length - 1]?.date ?? null;
  }, [annualIncomeCols]);

  const latestAnnualYear = useMemo(() => {
    if (!latestAnnualDateKey) return null;
    const y = new Date(latestAnnualDateKey).getFullYear();
    return Number.isFinite(y) ? y : null;
  }, [latestAnnualDateKey]);

  const latestQuarterDateForAnnualYear = useMemo(() => {
    if (latestAnnualYear == null) return null;
    const matches = quarterlyIncomeCols.filter((q) => {
      const y = new Date(q.date).getFullYear();
      return Number.isFinite(y) && y === latestAnnualYear;
    });
    if (matches.length === 0) return null;
    return matches[matches.length - 1]?.date ?? null;
  }, [latestAnnualYear, quarterlyIncomeCols]);

  const getHighlightedQuarterDate = useCallback(
    (quarters: Array<{ date: string }>) => {
      if (latestAnnualDateKey) {
        const match = quarters.find((q) => q.date === latestAnnualDateKey);
        if (match) return latestAnnualDateKey;
      }
      return latestQuarterDateForAnnualYear;
    },
    [latestAnnualDateKey, latestQuarterDateForAnnualYear]
  );

  const highlightedQuarterDate = useMemo(
    () => getHighlightedQuarterDate(quarterlyIncomeCols),
    [getHighlightedQuarterDate, quarterlyIncomeCols]
  );

  const highlightedQuarterDateForProfit = useMemo(
    () => getHighlightedQuarterDate(quarterlyIncomeCols),
    [getHighlightedQuarterDate, quarterlyIncomeCols]
  );

  const roicAnnual = useMemo(() => (useFiscalData ? roicRatios.filter((r) => r.periodType === 'annual') : []), [roicRatios, useFiscalData]);
  const roicQuarter = useMemo(() => (useFiscalData ? roicRatios.filter((r) => r.periodType === 'quarter') : []), [roicRatios, useFiscalData]);

  const fmpRoicAnnual = useMemo(() => {
    return fmpKeyMetricsAnnual
      .filter((row) => row?.date)
      .map((row) => {
        const raw = (row as any)?.returnOnInvestedCapital ?? (row as any)?.roic;
        const num = typeof raw === 'number' ? raw : Number(raw);
        return { date: row.date, periodType: 'annual' as const, value: Number.isFinite(num) ? num : undefined };
      });
  }, [fmpKeyMetricsAnnual]);

  const fmpRoicQuarter = useMemo(() => {
    return fmpKeyMetricsQuarter
      .filter((row) => row?.date)
      .map((row) => {
        const raw = (row as any)?.returnOnInvestedCapital ?? (row as any)?.roic;
        const num = typeof raw === 'number' ? raw : Number(raw);
        return { date: row.date, periodType: 'quarter' as const, value: Number.isFinite(num) ? num : undefined };
      });
  }, [fmpKeyMetricsQuarter]);

  const roicQuarterCombined = useMemo(() => {
    const map = new Map<string, FiscalRatioRow>();
    for (const row of fmpRoicQuarter) {
      if (row?.date) map.set(row.date, row);
    }
    for (const row of roicQuarter) {
      if (!row?.date) continue;
      const base = map.get(row.date);
      map.set(row.date, { ...row, value: isFiniteNumber(row.value) ? row.value : base?.value });
    }
    return Array.from(map.values());
  }, [fmpRoicQuarter, roicQuarter]);

  const roicQuarterCombinedEffective = useMemo(
    () => filterByVisibleRange(roicQuarterCombined),
    [filterByVisibleRange, roicQuarterCombined]
  );

  const getCashByDate = (date: string, cashArr: FiscalCashRow[]) => cashArr.find((cf) => cf.date === date);

  const getCashFreeCashFlow = (row: FiscalCashRow | null | undefined) => {
    if (!row) return undefined;
    const direct = (row as any).freeCashFlow;
    if (typeof direct === 'number' && Number.isFinite(direct)) return direct;

    const ocf = row.netCashProvidedByOperatingActivities ?? row.operatingCashFlow;
    if (typeof ocf !== 'number' || !Number.isFinite(ocf)) return undefined;

    const capex = row.capitalExpenditure ?? row.investmentsInPropertyPlantAndEquipment;
    if (typeof capex !== 'number' || !Number.isFinite(capex)) return undefined;

    return ocf + capex;
  };

  const calcPercentRatio = (numerator: number | undefined, denominator: number | undefined) => {
    if (typeof numerator !== 'number' || !Number.isFinite(numerator)) return undefined;
    if (typeof denominator !== 'number' || !Number.isFinite(denominator) || denominator === 0) return undefined;
    const ratio = (numerator / denominator) * 100;
    return Number.isFinite(ratio) ? ratio : undefined;
  };

  const formatCashToNetIncomeRatioDisplay = (numerator: number | undefined, denominator: number | undefined) => {
    if (typeof numerator !== 'number' || !Number.isFinite(numerator)) return { value: '-', isNA: false };
    if (typeof denominator !== 'number' || !Number.isFinite(denominator) || denominator === 0) return { value: '-', isNA: false };
    if (numerator < 0 || denominator < 0) return { value: 'N/A', isNA: true };

    const ratio = calcPercentRatio(numerator, denominator);
    if (typeof ratio !== 'number') return { value: '-', isNA: false };
    return { value: formatPercentFromAlreadyPercent(ratio, 1), isNA: false };
  };

  const normalizeRoicFraction = (value: number | undefined) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
    return Math.abs(value) > 1 ? value / 100 : value;
  };

  const getRoicByDate = (targetDate: string, period: 'annual' | 'quarter') => {
    const source = period === 'annual' ? roicAnnual : roicQuarter;
    if (!targetDate) return undefined;

    const maybeAnnualizeQuarterly = (value: number | undefined) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
      if (period === 'quarter' && Math.abs(value) < 1) return value * 4;
      return value;
    };

    if (source.length) {
      const exact = source.find((r) => r.date === targetDate);
      if (exact?.value != null) return maybeAnnualizeQuarterly(exact.value);

      const targetLabel = formatPeriodLabel(targetDate);
      const sameLabel = source.filter((r) => r.date && formatPeriodLabel(r.date) === targetLabel);
      if (sameLabel.length) return maybeAnnualizeQuarterly(sameLabel[0]?.value);
    }

    const targetLabel = formatPeriodLabel(targetDate);

    const fallback = (period === 'annual' ? fmpRoicAnnual : fmpRoicQuarter) as FiscalRatioRow[];
    for (const r of fallback) {
      if (r.date === targetDate || formatPeriodLabel(r.date) === targetLabel) return maybeAnnualizeQuarterly(r.value);
    }
    return undefined;
  };

  const formatRoicValue = (value: number | undefined) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
    return Math.abs(value) > 1 ? formatPercentFromAlreadyPercent(value, 1) : formatPercent(value, 1);
  };

  const getQuarterlyTTM = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ) => {
    const useDirectTTM = !asOfDate;

    if (field === 'operatingCashFlow') {
      if (useDirectTTM) {
        const direct = mergedCashTtm?.netCashProvidedByOperatingActivities ?? mergedCashTtm?.operatingCashFlow;
        if (typeof direct === 'number' && Number.isFinite(direct)) return formatStatementNumber(direct);
      }

      const last4Cash = quarterlyCashDesc.slice(0, 4);
      const ttm = sumQuarterlyCashField(last4Cash);
      return typeof ttm === 'number' && Number.isFinite(ttm) ? formatStatementNumber(ttm) : '-';
    }

    if (useDirectTTM && mergedIncomeTtm) {
      if (field === 'revenue' && typeof mergedIncomeTtm.revenue === 'number') return formatStatementNumber(mergedIncomeTtm.revenue);
      if (field === 'grossProfit' && typeof mergedIncomeTtm.grossProfit === 'number') return formatStatementNumber(mergedIncomeTtm.grossProfit);
      if (field === 'operatingIncome' && typeof mergedIncomeTtm.operatingIncome === 'number') return formatStatementNumber(mergedIncomeTtm.operatingIncome);
      if (field === 'netIncome' && typeof mergedIncomeTtm.netIncome === 'number') return formatStatementNumber(mergedIncomeTtm.netIncome);
      if (field === 'eps') {
        const epsVal = mergedIncomeTtm.epsDiluted ?? mergedIncomeTtm.eps;
        if (typeof epsVal === 'number' && Number.isFinite(epsVal)) return formatStatementNumber(epsVal);
      }
    }

    const last4Income = quarterlyIncomeDesc.slice(0, 4);
    const ttm = sumQuarterlyIncomeField(last4Income, field as any);
    return typeof ttm === 'number' && Number.isFinite(ttm) ? formatStatementNumber(ttm) : '-';
  };

  const sumQuarterlyIncomeField = (
    rows: FiscalIncomeRow[],
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps'
  ) => {
    const nums = rows
      .map((q) => {
        if (field === 'revenue') return q.revenue;
        if (field === 'grossProfit') return q.grossProfit;
        if (field === 'operatingIncome') return q.operatingIncome;
        if (field === 'netIncome') return q.netIncome;
        return q.epsDiluted ?? q.eps;
      })
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (nums.length < 4) return undefined;
    return nums.reduce((a, b) => a + b, 0);
  };

  const sumQuarterlyCashField = (rows: FiscalCashRow[]) => {
    const nums = rows
      .map((q) => q.netCashProvidedByOperatingActivities ?? q.operatingCashFlow)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (nums.length < 4) return undefined;
    return nums.reduce((a, b) => a + b, 0);
  };

  const sumQuarterlyFreeCashFlowField = (rows: FiscalCashRow[]) => {
    const nums = rows
      .map((q) => getCashFreeCashFlow(q))
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (nums.length < 4) return undefined;
    return nums.reduce((a, b) => a + b, 0);
  };

  const getLtmValue = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ) => {
    if (!mergedIncomeTtm && !mergedCashTtm) return undefined;
    if (field === 'operatingCashFlow') {
      if (!mergedCashTtm) return undefined;
      return mergedCashTtm.netCashProvidedByOperatingActivities ?? mergedCashTtm.operatingCashFlow;
    }

    if (!mergedIncomeTtm) return undefined;
    if (field === 'revenue') return mergedIncomeTtm.revenue;
    if (field === 'grossProfit') return mergedIncomeTtm.grossProfit;
    if (field === 'operatingIncome') return mergedIncomeTtm.operatingIncome;
    if (field === 'netIncome') return mergedIncomeTtm.netIncome;
    return mergedIncomeTtm.epsDiluted ?? mergedIncomeTtm.eps;
  };

  const getPriorAnnualValue = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ) => {
    const priorIncome = annualIncomeDesc[1];
    if (!priorIncome) return undefined;
    if (field === 'operatingCashFlow') {
      const priorCash = getCashByDate(priorIncome.date, mergedCashAnnualEffective);
      return priorCash?.netCashProvidedByOperatingActivities ?? priorCash?.operatingCashFlow;
    }

    if (field === 'revenue') return priorIncome.revenue;
    if (field === 'grossProfit') return priorIncome.grossProfit;
    if (field === 'operatingIncome') return priorIncome.operatingIncome;
    if (field === 'netIncome') return priorIncome.netIncome;
    return priorIncome.epsDiluted ?? priorIncome.eps;
  };

  const getLtmVsPriorLtmPercent = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ) => {
    const incomeDesc = quarterlyIncomeDesc;
    const cashDesc = quarterlyCashDesc;

    const currentIncome4 = incomeDesc.slice(0, 4);
    const priorIncome4 = incomeDesc.slice(4, 8);
    const currentCash4 = cashDesc.slice(0, 4);
    const priorCash4 = cashDesc.slice(4, 8);

    const current =
      field === 'operatingCashFlow'
        ? sumQuarterlyCashField(currentCash4)
        : sumQuarterlyIncomeField(currentIncome4, field);

    const prior =
      field === 'operatingCashFlow'
        ? sumQuarterlyCashField(priorCash4)
        : sumQuarterlyIncomeField(priorIncome4, field);

    if (typeof current !== 'number' || typeof prior !== 'number' || prior === 0) return '-';

    const pct = ((current / prior) - 1) * 100;
    if (!Number.isFinite(pct)) return '-';
    return formatPercentFromAlreadyPercent(pct, 1);
  };

  const getLatestAnnualValue = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ): number | undefined => {
    const latestIncome = annualIncomeAsc[annualIncomeAsc.length - 1];
    if (!latestIncome) return undefined;

    if (field === 'operatingCashFlow') {
      const latestCash = getCashByDate(latestIncome.date, mergedCashAnnualEffective);
      const ocf = latestCash?.netCashProvidedByOperatingActivities ?? latestCash?.operatingCashFlow;
      return typeof ocf === 'number' && Number.isFinite(ocf) ? ocf : undefined;
    }

    if (field === 'revenue') return latestIncome.revenue;
    if (field === 'grossProfit') return latestIncome.grossProfit;
    if (field === 'operatingIncome') return latestIncome.operatingIncome;
    if (field === 'netIncome') return latestIncome.netIncome;
    const eps = latestIncome.epsDiluted ?? latestIncome.eps;
    return typeof eps === 'number' && Number.isFinite(eps) ? eps : undefined;
  };

  const getCurrentTtmValue = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ): number | undefined => {
    const useDirectTTM = !asOfDate;

    if (field === 'operatingCashFlow') {
      if (useDirectTTM) {
        const direct = mergedCashTtm?.netCashProvidedByOperatingActivities ?? mergedCashTtm?.operatingCashFlow;
        if (typeof direct === 'number' && Number.isFinite(direct)) return direct;
      }
      const last4Cash = quarterlyCashDesc.slice(0, 4);
      return sumQuarterlyCashField(last4Cash);
    }

    if (useDirectTTM && mergedIncomeTtm) {
      if (field === 'revenue' && typeof mergedIncomeTtm.revenue === 'number') return mergedIncomeTtm.revenue;
      if (field === 'grossProfit' && typeof mergedIncomeTtm.grossProfit === 'number') return mergedIncomeTtm.grossProfit;
      if (field === 'operatingIncome' && typeof mergedIncomeTtm.operatingIncome === 'number') return mergedIncomeTtm.operatingIncome;
      if (field === 'netIncome' && typeof mergedIncomeTtm.netIncome === 'number') return mergedIncomeTtm.netIncome;
      if (field === 'eps') {
        const eps = mergedIncomeTtm.epsDiluted ?? mergedIncomeTtm.eps;
        if (typeof eps === 'number' && Number.isFinite(eps)) return eps;
      }
    }

    const last4Income = quarterlyIncomeDesc.slice(0, 4);
    return sumQuarterlyIncomeField(last4Income, field as any);
  };

  const isLatestAnnualEqualToCurrentTtm = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ): boolean => {
    const annual = getLatestAnnualValue(field);
    const ttm = getCurrentTtmValue(field);
    if (typeof annual !== 'number' || !Number.isFinite(annual)) return false;
    if (typeof ttm !== 'number' || !Number.isFinite(ttm)) return false;

    // If both values render identically in the table, treat them as equal.
    const annualDisplay = formatStatementNumber(annual);
    const ttmDisplay = formatStatementNumber(ttm);
    if (annualDisplay !== '-' && annualDisplay === ttmDisplay) return true;

    const absAnnual = Math.abs(annual);
    const tolerance = Math.max(1e-6, absAnnual * 0.001); // 0.1% tolerance
    return Math.abs(annual - ttm) <= tolerance;
  };

  const getQuarterlyTTMMargin = (kind: 'gross' | 'operating' | 'net') => {
    const useDirectTTM = !asOfDate;
    if (useDirectTTM && mergedIncomeTtm?.revenue && Number.isFinite(mergedIncomeTtm.revenue) && mergedIncomeTtm.revenue !== 0) {
      const denom = mergedIncomeTtm.revenue;
      const num =
        kind === 'gross'
          ? mergedIncomeTtm.grossProfit
          : kind === 'operating'
            ? mergedIncomeTtm.operatingIncome
            : mergedIncomeTtm.netIncome;
      if (typeof num === 'number' && Number.isFinite(num)) {
        return formatPercentFromAlreadyPercent((num / denom) * 100, 1);
      }
    }

    const last4 = quarterlyIncomeDesc.slice(0, 4);
    if (last4.length < 4) return '-';
    const revenue = last4.map((q) => q.revenue).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (revenue.length < 4) return '-';
    const num = last4
      .map((q) => {
        if (kind === 'gross') return q.grossProfit;
        if (kind === 'operating') return q.operatingIncome;
        return q.netIncome;
      })
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (num.length < 4) return '-';
    const denom = revenue.reduce((a, b) => a + b, 0);
    if (!denom) return '-';
    return formatPercentFromAlreadyPercent((num.reduce((a, b) => a + b, 0) / denom) * 100, 1);
  };

  const getQuarterlyTTMCashToNetIncomeRatio = (kind: 'ocf' | 'fcf') => {
    const useDirectTTM = !asOfDate;
    const ttmNetIncome = mergedIncomeTtm?.netIncome;
    const ttmNumerator =
      kind === 'ocf'
        ? mergedCashTtm?.netCashProvidedByOperatingActivities ?? mergedCashTtm?.operatingCashFlow
        : getCashFreeCashFlow(mergedCashTtm ?? undefined);

    if (useDirectTTM) {
      const ttmDisplay = formatCashToNetIncomeRatioDisplay(ttmNumerator, ttmNetIncome);
      if (ttmDisplay.value !== '-') return ttmDisplay;
    }

    const last4Income = quarterlyIncomeDesc.slice(0, 4);
    const last4Cash = quarterlyCashDesc.slice(0, 4);
    if (last4Income.length < 4 || last4Cash.length < 4) return { value: '-', isNA: false };

    const netIncome = sumQuarterlyIncomeField(last4Income, 'netIncome');
    const numerator = kind === 'ocf' ? sumQuarterlyCashField(last4Cash) : sumQuarterlyFreeCashFlowField(last4Cash);
    return formatCashToNetIncomeRatioDisplay(numerator, netIncome);
  };
  
  const calcCMGR = (start: number, end: number, years: number) => {
    if (!Number.isFinite(start) || !Number.isFinite(end) || years <= 0) return 'N/A';
    if (start <= 0 || end <= 0) return 'N/A';
    const ratio = end / start;
    const cagr = (Math.pow(ratio, 1 / years) - 1) * 100;
    if (!Number.isFinite(cagr)) return 'N/A';
    return `${cagr.toFixed(1)}%`;
  };
  
  const getAnnualSeries = (
    incomeRows: FiscalIncomeRow[],
    cashRows: FiscalCashRow[],
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ) => {
    return incomeRows
      .map((row: FiscalIncomeRow) => {
        if (field === 'operatingCashFlow') {
          const cash = getCashByDate(row.date, cashRows);
          return cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
        }
        if (field === 'revenue') return row.revenue;
        if (field === 'grossProfit') return row.grossProfit;
        if (field === 'operatingIncome') return row.operatingIncome;
        if (field === 'netIncome') return row.netIncome;
        return row.epsDiluted ?? row.eps;
      })
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  };
  
  const getCMGRValue = (
    incomeRows: FiscalIncomeRow[],
    cashRows: FiscalCashRow[],
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow',
    years: number
  ) => {
    const values = getAnnualSeries(incomeRows, cashRows, field);
    if (values.length < years + 1) return 'N/A';
    const start = values[values.length - (years + 1)];
    const end = values[values.length - 1];
    return calcCMGR(start, end, years);
  };
  
  const lastAnnualYear = useMemo(() => {
    if (annualIncomeAsc.length === 0) return new Date().getFullYear();
    const last = annualIncomeAsc[annualIncomeAsc.length - 1]?.date;
    const y = last ? new Date(last).getFullYear() : new Date().getFullYear();
    return Number.isFinite(y) ? y : new Date().getFullYear();
  }, [annualIncomeAsc]);

  const isEmbeddedBackTesting = mode === 'embedded' && Boolean(asOfDate);

  const ttmLabelDate = useMemo(() => {
    const asOfKey = toUtcDateKey(String(asOfDate || ''));

    if (useAtReportedAnchors && isEmbeddedBackTesting && asOfKey) {
      const asOfTs = Date.parse(`${asOfKey}T00:00:00.000Z`);
      if (Number.isFinite(asOfTs)) {
        // Generic filing-date windows for all symbols, using the real SEC filing
        // date from /v2/company/filings (matched by reported period-end date).
        // Fallback to the row's own filingDate only when no real filing is found.
        const candidates = quarterlyIncomeDescRaw
          .map((row) => {
            const reportKey = toUtcDateKey(String(row?.date || ''));
            if (!reportKey) return null;
            const realFilingKey = filingDateByReportKey.get(reportKey);
            const rowFilingKey = toUtcDateKey(String(row?.filingDate || row?.date || ''));
            const filingKey = hasRealFilingDateMap
              ? realFilingKey
              : rowFilingKey;
            if (!filingKey) return null;
            const filingTs = Date.parse(`${filingKey}T00:00:00.000Z`);
            if (!Number.isFinite(filingTs)) return null;
            return { reportKey, filingTs };
          })
          .filter((x): x is { reportKey: string; filingTs: number } => !!x)
          .sort((a, b) => b.filingTs - a.filingTs);

        const active = candidates.find((x) => x.filingTs <= asOfTs);
        if (active?.reportKey) return active.reportKey;
      }
    }

    // TTM is computed from the latest 4 available quarters, so label should point
    // to the most recently reported quarter — even if it is ahead of the latest annual.
    const latestAvailableQuarter = quarterlyIncomeDesc[0]?.date;
    if (latestAvailableQuarter) return latestAvailableQuarter;

    const quarterLabel = highlightedQuarterDate || latestQuarterDateForAnnualYear;
    if (quarterLabel) return quarterLabel;
    if (mergedIncomeTtm?.date) return mergedIncomeTtm.date;
    if (mergedCashTtm?.date) return mergedCashTtm.date;
    if (isEmbeddedBackTesting && asOfDate) return String(asOfDate);
    return '';
  }, [
    asOfDate,
    filingDateByReportKey,
    hasRealFilingDateMap,
    highlightedQuarterDate,
    isEmbeddedBackTesting,
    latestQuarterDateForAnnualYear,
    mergedCashTtm?.date,
    mergedIncomeTtm?.date,
    quarterlyIncomeDesc,
    quarterlyIncomeDescRaw,
    rangeChangeSource,
    useAtReportedAnchors,
  ]);

  const roicTtmDisplay = useMemo(() => {
    const values = sortByDateDesc(roicQuarterCombinedEffective)
      .slice(0, 4)
      .map((r) => normalizeRoicFraction(r.value))
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (values.length < 4) return '-';
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return formatPercent(avg, 1);
  }, [roicQuarterCombinedEffective]);

  if (loading) {
    return (
      <div style={containerStyle}>
        <CenteredLoadingSpinner isDarkMode={isDarkMode} minHeight={mode === 'page' ? 'calc(100vh - 15rem)' : '240px'} />
      </div>
    );
  }

  const pageContent = (
    <div id={mode === 'page' ? 'growth-fiscal-main-container' : 'growth-fiscal-tables-embedded'} style={frameStyle}>
      {useFiscalData && errorMessage && (
        <div style={errorBannerStyle}>
          Fiscal API: {errorMessage}
        </div>
      )}
      <div
        id="growth-fiscal-calc-analyst-row"
        style={{
          display: 'flex',
          gap: '2.5rem',
          marginBottom: '0rem',
          background: isDarkMode ? '#111827' : '#ffffff',
          position: 'relative',
          paddingRight: '2.25rem',
        }}
      >
        <div style={{ flexGrow: 2, flexShrink: 1, flexBasis: 0, minWidth: 0 }}>
          <div style={{ ...sectionTitleStyle, position: 'relative' }}>
            Growth Calculation (CAGR)
            {showSourceLabels ? (
              <span style={floatingSourceLabelStyle}>{useFiscalData ? 'fiscal.ai' : 'FMP'}</span>
            ) : null}
          </div>
          <table id="growth-fiscal-cmgr-table" style={{ ...tableStyle, tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={firstColHeaderStyle}>Fiscal Period</th>
                <th style={thStyle}>{`5 Years (${lastAnnualYear - 5}-${lastAnnualYear})`}</th>
                <th style={thStyle}>{`3 Years (${lastAnnualYear - 3}-${lastAnnualYear})`}</th>
                <th style={thStyle}>{`1 Year (${lastAnnualYear - 1}-${lastAnnualYear})`}</th>
                <th
                  style={thStyle}
                >{`TTM / ${formatPeriodLabel(ttmLabelDate || '')}`}</th>
              </tr>
            </thead>
            <tbody>
              {['revenue', 'grossProfit', 'operatingIncome', 'netIncome', 'eps', 'operatingCashFlow'].map((field) => (
                <tr key={field}>
                  <td style={leftTdStyle}>
                    {field === 'revenue' && 'Revenue'}
                    {field === 'grossProfit' && 'Gross Profit'}
                    {field === 'operatingIncome' && 'Operating Income'}
                    {field === 'netIncome' && 'Net Income'}
                    {field === 'eps' && 'EPS (Diluted)'}
                    {field === 'operatingCashFlow' && 'Operating Cash Flow'}
                  </td>
                  {(() => {
                    const v5 = normalizeCagrTableValue(getCMGRValue(annualIncomeAsc, mergedCashAnnualEffective, field as any, 5));
                    const v3 = normalizeCagrTableValue(getCMGRValue(annualIncomeAsc, mergedCashAnnualEffective, field as any, 3));
                    const v1Raw = getCMGRValue(annualIncomeAsc, mergedCashAnnualEffective, field as any, 1);
                    const vTtmRaw = getLtmVsPriorLtmPercent(field as any);

                    // When the latest annual value equals current TTM value,
                    // 1Y and TTM growth should match in the table.
                    const shouldAlignOneYearAndTtm = isLatestAnnualEqualToCurrentTtm(field as any);
                    let finalVTtmRaw = vTtmRaw;
                    if (shouldAlignOneYearAndTtm) {
                      const priorAnnual = getPriorAnnualValue(field as any);
                      const currentAnnual = getLatestAnnualValue(field as any);
                      if (typeof priorAnnual === 'number' && typeof currentAnnual === 'number') {
                        finalVTtmRaw = calcCMGR(priorAnnual, currentAnnual, 1);
                      } else {
                        finalVTtmRaw = v1Raw;
                      }
                    }
                    const v1 = normalizeCagrTableValue(v1Raw);
                    const vTtm = normalizeCagrTableValue(finalVTtmRaw);

                    const cagrNAStyle: React.CSSProperties = { color: '#ff0000' };
                    const styleFor = (v: string) => (v === 'N/A' ? cagrNAStyle : getPercentCellStyle(v));
                    return (
                      <>
                        {(() => {
                          const s = { ...tdStyle, ...styleFor(v5) };
                          return <td style={s}>{renderWithWarningIcon(v5, s)}</td>;
                        })()}
                        {(() => {
                          const s = { ...tdStyle, ...styleFor(v3) };
                          return <td style={s}>{renderWithWarningIcon(v3, s)}</td>;
                        })()}
                        {(() => {
                          const s = { ...tdStyle, ...styleFor(v1) };
                          return <td style={s}>{renderWithWarningIcon(v1, s)}</td>;
                        })()}
                        {(() => {
                          const s = { ...tdStyle, ...styleFor(vTtm) };
                          return <td style={s}>{renderWithWarningIcon(vTtm, s)}</td>;
                        })()}
                      </>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ResearchGrowthTables
          isDarkMode={isDarkMode}
          selectedStock={selectedStock}
          selectedPeriod="annual"
          mode="embedded"
          analysisDateMode={analysisDateMode}
          asOfDate={asOfDate}
          rangeStart={rangeStart}
          sections="analyst-panel"
          useFrame={false}
          analystTitleSuffix=""
          showSourceLabel={showSourceLabels}
          sourceLabel="FMP"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${firstColWidthPx}px repeat(${annualIncomeCols.length}, 1fr) 1fr repeat(${quarterlyIncomeCols.length}, 1fr)`,
          marginBottom: 4,
          position: 'relative',
          paddingRight: '2.25rem',
        }}
      >
        {showSourceLabels ? (
          <span
            style={{
              ...floatingSourceLabelStyle,
              right: mode === 'page' && showBookmarkButton ? '2.25rem' : 0,
            }}
          >
            {useFiscalData ? 'fiscal.ai' : 'FMP'}
          </span>
        ) : null}
        {mode === 'page' && showBookmarkButton && (
          <div
            style={{
              position: 'absolute',
              right: '0.25rem',
              top: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              zIndex: 2,
            }}
          >
            {isBookmarkBusy && (
              <span
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: isDarkMode ? '#9CA3AF' : '#6B7280',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Saving...
              </span>
            )}
            <button
              style={{
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                outline: 'none',
                boxShadow: 'none',
                opacity: isBookmarkBusy ? 0.6 : 1,
                transition: 'background-color 120ms ease',
              }}
              title="Bookmark"
              disabled={isBookmarkBusy}
              onClick={onBookmarkClick ?? (() => undefined)}
            >
              <img
                src={isBookmarked ? '/images/bookmark_fill.svg' : (isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg')}
                alt="Bookmark"
                style={{
                  width: '20px',
                  height: '20px',
                }}
              />
            </button>
          </div>
        )}
        <div style={groupHeaderTitleStyle}>Financials</div>
        <div style={{ ...groupHeaderTitleStyle, gridColumn: `2 / span ${annualIncomeCols.length}`, marginLeft: 2 }}>Annuals</div>
        <div style={{ gridColumn: `${2 + annualIncomeCols.length} / span 1` }} />
        <div
          style={{
            ...groupHeaderTitleStyle,
            gridColumn: `${3 + annualIncomeCols.length} / span ${quarterlyIncomeCols.length}`,
            marginLeft: 26,
          }}
        >
          Quarterly
        </div>
      </div>

      <table id="growth-fiscal-financials-table" style={{ ...combinedTableStyle, marginBottom: '0.85rem' }}>
        <thead>
          <tr>
            <th style={firstColHeaderStyle}>Fiscal Period</th>
            {annualIncomeCols.map((d, idx) => (
              <th
                style={
                  idx === annualIncomeCols.length - 1
                    ? {
                        ...thStyle,
                        borderRight: ttmDividerBorder,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                    : {
                        ...thStyle,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                }
                key={`a-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
            <th style={{ ...thStyle, borderLeft: ttmDividerBorder, borderRight: dividerBorder }}>TTM</th>
            {quarterlyIncomeCols.map((d, idx) => (
              <th
                style={
                  idx === 0
                    ? {
                        ...thStyle,
                        borderLeft: dividerBorder,
                        fontWeight: highlightedQuarterDate && d.date === highlightedQuarterDate ? 800 : thStyle.fontWeight,
                      }
                    : {
                        ...thStyle,
                        fontWeight: highlightedQuarterDate && d.date === highlightedQuarterDate ? 800 : thStyle.fontWeight,
                      }
                }
                key={`q-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {['revenue', 'grossProfit', 'operatingIncome', 'netIncome', 'eps', 'operatingCashFlow'].map((field) => (
            <tr key={field}>
              <td style={firstColCellStyle}>
                {field === 'revenue' && 'Revenue'}
                {field === 'grossProfit' && 'Gross Profit'}
                {field === 'operatingIncome' && 'Operating Income'}
                {field === 'netIncome' && 'Net Income'}
                {field === 'eps' && 'EPS (Diluted)'}
                {field === 'operatingCashFlow' && 'Operating Cash Flow'}
              </td>

              {annualIncomeCols.map((d, idx) => {
                const cash = getCashByDate(d.date, annualCashCols);
                const value =
                  field === 'revenue'
                    ? d.revenue
                    : field === 'grossProfit'
                      ? d.grossProfit
                      : field === 'operatingIncome'
                        ? d.operatingIncome
                        : field === 'netIncome'
                          ? d.netIncome
                          : field === 'eps'
                            ? d.epsDiluted ?? d.eps
                            : cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
                const cellStyle =
                  idx === annualIncomeCols.length - 1
                    ? { ...tdStyle, borderRight: ttmDividerBorder, ...getNegativeNumberCellStyle(value) }
                    : { ...tdStyle, ...getNegativeNumberCellStyle(value) };
                return (
                  <td style={cellStyle} key={`a-${d.date}`}>
                    {formatStatementNumber(value)}
                  </td>
                );
              })}

              {(() => {
                const rendered = getQuarterlyTTM(field as any);
                const cellStyle = { ...tdStyle, borderLeft: ttmDividerBorder, borderRight: dividerBorder, ...getNegativeStringCellStyle(rendered) };
                return <td style={cellStyle}>{rendered}</td>;
              })()}

              {quarterlyIncomeCols.map((d, idx) => {
                const cash = getCashByDate(d.date, quarterlyCashCols);
                const value =
                  field === 'revenue'
                    ? d.revenue
                    : field === 'grossProfit'
                      ? d.grossProfit
                      : field === 'operatingIncome'
                        ? d.operatingIncome
                        : field === 'netIncome'
                          ? d.netIncome
                          : field === 'eps'
                            ? d.epsDiluted ?? d.eps
                            : cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
                const cellStyle = idx === 0
                  ? { ...tdStyle, borderLeft: dividerBorder, ...getNegativeNumberCellStyle(value) }
                  : { ...tdStyle, ...getNegativeNumberCellStyle(value) };
                return (
                  <td style={cellStyle} key={`q-${d.date}`}>
                    {formatStatementNumber(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${firstColWidthPx}px repeat(${annualIncomeCols.length}, 1fr) 1fr repeat(${quarterlyIncomeCols.length}, 1fr)`,
          marginBottom: 4,
          position: 'relative',
        }}
      >
        {showSourceLabels ? (
          <span style={floatingSourceLabelStyle}>{useFiscalData ? 'fiscal.ai' : 'FMP'}</span>
        ) : null}
        <div style={groupHeaderTitleStyle}>Profitability</div>
        <div style={{ ...groupHeaderTitleStyle, gridColumn: `2 / span ${annualIncomeCols.length}`, marginLeft: 2 }}>Annuals</div>
        <div style={{ gridColumn: `${2 + annualIncomeCols.length} / span 1` }} />
        <div
          style={{
            ...groupHeaderTitleStyle,
            gridColumn: `${3 + annualIncomeCols.length} / span ${quarterlyIncomeCols.length}`,
            marginLeft: 4,
          }}
        >
          Quarterly
        </div>
      </div>
      <table id="growth-fiscal-profitability-table" style={{ ...combinedTableStyle, marginBottom: '0.45rem' }}>
        <thead>
          <tr>
            <th style={firstColHeaderStyle}>Fiscal Period</th>
            {annualIncomeCols.map((d, idx) => (
              <th
                style={
                  idx === annualIncomeCols.length - 1
                    ? {
                        ...thStyle,
                        borderRight: ttmDividerBorder,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                    : {
                        ...thStyle,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                }
                key={`a-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
            <th style={{ ...thStyle, borderLeft: ttmDividerBorder }}>TTM</th>
            {quarterlyIncomeCols.map((d) => (
              <th
                style={{
                  ...thStyle,
                  fontWeight: highlightedQuarterDateForProfit && d.date === highlightedQuarterDateForProfit ? 800 : thStyle.fontWeight,
                }}
                key={`q-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={firstColCellStyle}>Gross Profit Margin</td>
            {annualIncomeCols.map((d, idx) => {
              const pct = d.revenue ? (d.grossProfit ?? 0) / d.revenue * 100 : undefined;
              const cellStyle =
                idx === annualIncomeCols.length - 1
                  ? { ...tdStyle, borderRight: ttmDividerBorder, ...getNegativeNumberCellStyle(pct) }
                  : { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const v = getQuarterlyTTMMargin('gross');
              const cellStyle = { ...tdStyle, borderLeft: ttmDividerBorder, ...getNegativeStringCellStyle(v) };
              return <td style={cellStyle}>{v}</td>;
            })()}
            {quarterlyIncomeCols.map((d) => {
              const pct = d.revenue ? (d.grossProfit ?? 0) / d.revenue * 100 : undefined;
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              const cellStyle = { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>Operating Income Margin</td>
            {annualIncomeCols.map((d, idx) => {
              const pct = d.revenue ? (d.operatingIncome ?? 0) / d.revenue * 100 : undefined;
              const cellStyle =
                idx === annualIncomeCols.length - 1
                  ? { ...tdStyle, borderRight: ttmDividerBorder, ...getNegativeNumberCellStyle(pct) }
                  : { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const v = getQuarterlyTTMMargin('operating');
              const cellStyle = { ...tdStyle, borderLeft: ttmDividerBorder, ...getNegativeStringCellStyle(v) };
              return <td style={cellStyle}>{v}</td>;
            })()}
            {quarterlyIncomeCols.map((d) => {
              const pct = d.revenue ? (d.operatingIncome ?? 0) / d.revenue * 100 : undefined;
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              const cellStyle = { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>Net Income Margin</td>
            {annualIncomeCols.map((d, idx) => {
              const pct = d.revenue ? (d.netIncome ?? 0) / d.revenue * 100 : undefined;
              const cellStyle =
                idx === annualIncomeCols.length - 1
                  ? { ...tdStyle, borderRight: ttmDividerBorder, ...getNegativeNumberCellStyle(pct) }
                  : { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const v = getQuarterlyTTMMargin('net');
              const cellStyle = { ...tdStyle, borderLeft: ttmDividerBorder, ...getNegativeStringCellStyle(v) };
              return <td style={cellStyle}>{v}</td>;
            })()}
            {quarterlyIncomeCols.map((d) => {
              const pct = d.revenue ? (d.netIncome ?? 0) / d.revenue * 100 : undefined;
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              const cellStyle = { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>ROIC</td>
            {annualIncomeCols.map((d, idx) => {
              const v = formatRoicValue(getRoicByDate(d.date, 'annual'));
              const cellStyle =
                idx === annualIncomeCols.length - 1
                  ? { ...tdStyle, borderRight: ttmDividerBorder, ...getNegativeStringCellStyle(v) }
                  : { ...tdStyle, ...getNegativeStringCellStyle(v) };
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {v}
                </td>
              );
            })}
            {(() => {
              const latestIncome = annualIncomeDesc[0];
              const latestRoic = latestIncome?.date ? formatRoicValue(getRoicByDate(latestIncome.date, 'annual')) : '-';
              const cellStyle = { ...tdStyle, borderLeft: ttmDividerBorder, ...getNegativeStringCellStyle(latestRoic) };
              return <td style={cellStyle}>{latestRoic}</td>;
            })()}
            {quarterlyIncomeCols.map((d) => {
              const v = formatRoicValue(getRoicByDate(d.date, 'quarter'));
              const cellStyle = { ...tdStyle, ...getNegativeStringCellStyle(v) };
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {v}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>OCF / Net Income</td>
            {annualIncomeCols.map((d, idx) => {
              const cash = getCashByDate(d.date, annualCashCols);
              const ocf = cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(ocf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle =
                idx === annualIncomeCols.length - 1
                  ? { ...tdStyle, borderRight: ttmDividerBorder, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) }
                  : { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`a-ocf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const ratioDisplay = getQuarterlyTTMCashToNetIncomeRatio('ocf');
              const cellStyle = {
                ...tdStyle,
                borderLeft: ttmDividerBorder,
                ...getNegativeStringCellStyle(ratioDisplay.value),
                ...(ratioDisplay.isNA ? { color: 'red' } : {}),
              };
              return <td style={cellStyle}>{ratioDisplay.value}</td>;
            })()}
            {quarterlyIncomeCols.map((d) => {
              const cash = getCashByDate(d.date, quarterlyCashCols);
              const ocf = cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(ocf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle = { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`q-ocf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>FCF / Net Income</td>
            {annualIncomeCols.map((d, idx) => {
              const cash = getCashByDate(d.date, annualCashCols);
              const fcf = getCashFreeCashFlow(cash);
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(fcf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle =
                idx === annualIncomeCols.length - 1
                  ? { ...tdStyle, borderRight: ttmDividerBorder, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) }
                  : { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`a-fcf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const ratioDisplay = getQuarterlyTTMCashToNetIncomeRatio('fcf');
              const cellStyle = {
                ...tdStyle,
                borderLeft: ttmDividerBorder,
                ...getNegativeStringCellStyle(ratioDisplay.value),
                ...(ratioDisplay.isNA ? { color: 'red' } : {}),
              };
              return <td style={cellStyle}>{ratioDisplay.value}</td>;
            })()}
            {quarterlyIncomeCols.map((d) => {
              const cash = getCashByDate(d.date, quarterlyCashCols);
              const fcf = getCashFreeCashFlow(cash);
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(fcf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle = { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`q-fcf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );

  return mode === 'page' ? (
    <div id="growth-fiscal-main-frame" style={{ paddingTop: '15px' }}>
      {pageContent}
    </div>
  ) : (
    pageContent
  );
};

export default ResearchGrowthFiscalTables;
